import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import HowToEngage from './HowToEngage';
import useSEO from '../hooks/useSEO';

function HospitalORExample({ logo }) {
  useSEO({
    title: 'Hospital OR Scheduling Simulation',
    description: 'Simulate patient flows and operating room schedules. End surgical delays and maximize throughput.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Scheduling + Throughput Optimization</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">End surgical delays and emergency bottlenecks</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">Simulate patient flows and operating room schedules to maximize throughput—before patients and staff pay the price.</p>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Who this is for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Hospital Administrators</p><p className="text-sm text-gray-600">Maximizing facility utilization and revenue</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Clinical Operations</p><p className="text-sm text-gray-600">Reducing wait times and improving flow</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Nursing Leadership</p><p className="text-sm text-gray-600">Balancing staffing with patient acuity</p></div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Decisions this improves</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How to schedule OR blocks for maximum throughput</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">When to add capacity vs. improve existing flow</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How many beds to hold for emergency admissions</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How staffing ratios affect patient outcomes</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Where bottlenecks form during surge periods</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Trade-offs between efficiency and care quality</p></div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <div className="bg-gray-900 text-white rounded-lg p-8 md:p-10">
            <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Example: Regional Hospital System</p>
            <p className="text-lg leading-relaxed mb-6">A regional hospital simulated OR scheduling patterns, patient acuity variations, and staffing rotations. The model revealed that staggering start times by 15 minutes and reserving one OR for emergencies would increase surgical throughput by 18%—while reducing overtime costs and improving staff satisfaction.</p>
            <p className="text-gray-400 text-sm">The impact: 340 additional procedures per year and $2.1M in recovered revenue.</p>
          </div>
        </div>

                <HowToEngage
          platformDescription="Model your patient flows, test scheduling scenarios, and share insights with leadership. Ideal for operations teams who want ongoing decision support."
          consultingDescription="Partner with our healthcare consultants to build a custom capacity model, run throughput workshops, and deliver implementation-ready recommendations."
        />

        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/solutions/workforce" className="text-gray-700 hover:text-gray-900 text-sm">Capacity + Backlog Planning →</Link>
            <Link to="/solutions/supply-chain" className="text-gray-700 hover:text-gray-900 text-sm">Inventory + Production →</Link>
            <Link to="/solutions/logistics-transportation" className="text-gray-700 hover:text-gray-900 text-sm">Route + Fleet Optimization →</Link>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default HospitalORExample;
